import type { GetCustomersOverviewsNewResponse } from '@vy/api-definition';
import { GetCustomersOverviewsNewResponseSchema } from '@vy/api-definition';
import type { WeftApi } from '@vy/client';
import type { QueryClient, UseQueryOptions } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';
import { getPrivateApi } from '../../../services/api';
import { queryKeys } from '../../../services/query-key/query-keys';

type UseCustomersOverviewsNewPayload = Parameters<WeftApi['getCustomersOverviewsNew']>[0];

export async function customersOverviewsNewQuery(
  payload: UseCustomersOverviewsNewPayload,
  signal?: AbortSignal,
) {
  const api = getPrivateApi();
  const response = await api.getCustomersOverviewsNew(payload, { signal });
  return GetCustomersOverviewsNewResponseSchema.parse(response);
}

export function useCustomersOverviewsNew(
  payload: UseCustomersOverviewsNewPayload = {},
  options?: UseQueryOptions<GetCustomersOverviewsNewResponse>,
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.customersOverviewsNew(...Object.values(payload)),
    queryFn: async ({ signal }) => {
      await cancelGetCustomersOverviewsNewQueries(queryClient);

      return customersOverviewsNewQuery(payload, signal);
    },
    ...options,
  });
}

export async function prefetchCustomersOverviewsNew(
  queryClient: QueryClient,
  payload: UseCustomersOverviewsNewPayload = {},
) {
  return queryClient.prefetchQuery(queryKeys.customersOverviewsNew(...Object.values(payload)), () =>
    customersOverviewsNewQuery(payload),
  );
}

async function cancelGetCustomersOverviewsNewQueries(queryClient: QueryClient) {
  await queryClient.cancelQueries({
    predicate: (query) => query.queryKey[0] === queryKeys.customersOverviewsNew()[0],
  });
}
